import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { completeSignUp } from '@/lib/api';
import { Button, ButtonLink, IconArrowRight, InlineNotification, Spinner } from '@/components/carbon';
import { FrameNote, PUBLIC_LINK, SignInFrame } from '@/components/PublicLayout';

export type CallbackStatus = 'processing' | 'success' | 'error' | 'already-set-up';

export interface ErrorInfo {
  title: string;
  message: string;
  canRetry: boolean;
}

const SignUpCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);

  useEffect(() => {
    let ignore = false;

    const processCallback = async () => {
      // Parse OAuth callback parameters from URL
      const tenantId = searchParams.get('tenant');
      const state = searchParams.get('state');
      const adminConsent = searchParams.get('admin_consent');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check if this is an error response from Azure AD
      // Special case: AADSTS650051 means service principal already exists, but consent was granted
      // This happens when re-consenting to an app that already has a service principal in the tenant
      const isServicePrincipalExistsError = errorDescription?.includes('AADSTS650051');
      const consentWasGranted = adminConsent === 'True' || adminConsent === 'true';

      if (error && !(isServicePrincipalExistsError && consentWasGranted)) {
        if (ignore) return;
        handleAzureError(error, errorDescription);
        return;
      }

      // If we get here with AADSTS650051 but admin_consent=True, continue processing
      // The service principal exists and consent was granted - this is actually success
      // Extract tenant ID from error description if not in URL params (Azure omits it on this error)
      let effectiveTenantId = tenantId;
      if (!effectiveTenantId && isServicePrincipalExistsError && errorDescription) {
        // Error description contains: "...for the tenant {guid}..."
        const tenantMatch = errorDescription.match(/tenant\s+([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
        if (tenantMatch) {
          effectiveTenantId = tenantMatch[1];
        }
      }

      // Validate required parameters
      if (!effectiveTenantId || !state) {
        if (ignore) return;
        setStatus('error');
        setErrorInfo({
          title: 'The sign-up link is incomplete.',
          message: 'Required details are missing from the link, so your sign-up session may have expired.',
          canRetry: true,
        });
        return;
      }

      try {
        // Send callback data to backend for verification
        const response = await completeSignUp({
          tenantId: effectiveTenantId,
          state,
          adminConsent: adminConsent === 'True' || adminConsent === 'true',
          error: error || undefined,
          errorDescription: errorDescription || undefined,
        });

        if (ignore) return;

        if (response.success && response.alreadySetUp) {
          // The tenant already had a Guardian row and nothing was changed, so this
          // is not a new sign-up: no "Consent granted" success page. The session
          // is closed server-side; drop the local copy too.
          localStorage.removeItem('guardian_signup_session');
          setStatus('already-set-up');
        } else if (response.success) {
          // Get sessionId from localStorage
          const sessionId = localStorage.getItem('guardian_signup_session');
          // Navigate to success page
          navigate(`/signup/success${sessionId ? `?session=${sessionId}` : ''}`);
        } else {
          setStatus('error');
          setErrorInfo({
            title: 'Your sign-up didn’t finish.',
            message: response.error || 'Guardian could not complete your sign-up.',
            canRetry: true,
          });
        }
      } catch (err) {
        if (ignore) return;
        setStatus('error');
        setErrorInfo({
          title: 'Guardian could not be reached.',
          message: err instanceof Error
            ? err.message
            : 'Check your internet connection, then try again.',
          canRetry: true,
        });
      }
    };

    processCallback();

    return () => {
      ignore = true;
    };
  }, [searchParams, navigate]);

  const handleAzureError = (error: string, errorDescription: string | null) => {
    setStatus('error');

    // Map Azure AD errors to user-friendly messages
    switch (error) {
      case 'access_denied':
        setErrorInfo({
          title: 'Consent was declined.',
          message: 'Guardian needs admin consent for its read permissions before it can connect to your tenant.',
          canRetry: true,
        });
        break;
      case 'consent_required':
        setErrorInfo({
          title: 'Admin consent is required.',
          message: 'Sign in with Global Administrator rights in your organization to grant it.',
          canRetry: true,
        });
        break;
      case 'invalid_request':
        setErrorInfo({
          title: 'The consent request was invalid.',
          message: errorDescription || 'Microsoft rejected the consent request.',
          canRetry: true,
        });
        break;
      case 'temporarily_unavailable':
        setErrorInfo({
          title: 'Microsoft is unavailable right now.',
          message: 'Try again in a few minutes.',
          canRetry: true,
        });
        break;
      case 'invalid_client':
      case 'unauthorized_client':
        setErrorInfo({
          title: 'Guardian has a configuration problem.',
          message: 'Contact support, and the Guardian team will fix it.',
          canRetry: false,
        });
        break;
      default:
        setErrorInfo({
          title: 'Microsoft sign-in failed.',
          message: errorDescription || `Microsoft returned the error "${error}".`,
          canRetry: true,
        });
    }
  };

  const handleRetry = () => {
    // Clear the stored session
    localStorage.removeItem('guardian_signup_session');
    // Navigate back to sign-up page
    navigate('/signup');
  };

  return <SignUpCallbackView status={status} errorInfo={errorInfo} onRetry={handleRetry} />;
};

export interface SignUpCallbackViewProps {
  status: CallbackStatus;
  errorInfo: ErrorInfo | null;
  onRetry: () => void;
}

/**
 * The callback page body (mockup carbon/04 frame). Processing is a live
 * status line; an error is a Coral InlineNotification with the steps to try
 * and, when the flow can restart, the one primary action. Success never
 * renders here: it forwards to /signup/success. Already set up is a neutral
 * sign-in page that never names the tenant: the tenant id came from the URL,
 * and the same page serves live, deactivated and revoked customers alike.
 * Pure, for SSR render tests.
 */
export function SignUpCallbackView({ status, errorInfo, onRetry }: SignUpCallbackViewProps) {
  if (status === 'already-set-up') {
    return (
      <SignInFrame
        brandTo="/signup"
        headline="Sign in to continue."
        lede="This tenant is already connected to Guardian."
      >
        <ButtonLink to="/" kind="primary" icon={<IconArrowRight />}>
          Continue to Guardian
        </ButtonLink>

        <FrameNote>
          <p className="m-0">
            If you can’t sign in, contact{' '}
            <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
              support@iuvotech.com
            </a>
            .
          </p>
        </FrameNote>
      </SignInFrame>
    );
  }

  if (status === 'error' && errorInfo) {
    return (
      <SignInFrame
        brandTo="/signup"
        headline={errorInfo.canRetry ? 'Start your sign-up again.' : 'Contact support to finish your sign-up.'}
      >
        <InlineNotification kind="error" title={errorInfo.title}>
          {errorInfo.message}
        </InlineNotification>

        <h2 className="m-0 mt-8 text-body font-semibold text-text-primary">What you can try</h2>
        <ul className="m-0 mt-2 list-disc space-y-1 pl-5 text-body text-text-secondary marker:text-text-helper">
          {errorInfo.canRetry && (
            <>
              <li>Start over with the button below.</li>
              <li>Sign in as a Global Administrator in your organization.</li>
              <li>Click &ldquo;Accept&rdquo; on the Microsoft consent screen.</li>
            </>
          )}
          <li>Contact support if the problem persists.</li>
        </ul>

        {errorInfo.canRetry && (
          <div className="mt-8">
            <Button kind="primary" onClick={onRetry} icon={<IconArrowRight />}>
              Try again
            </Button>
          </div>
        )}

        <FrameNote>
          <p className="m-0">
            Need help?{' '}
            <a href="mailto:support@iuvotech.com" className={PUBLIC_LINK}>
              support@iuvotech.com
            </a>
          </p>
        </FrameNote>
      </SignInFrame>
    );
  }

  return (
    <SignInFrame
      brandTo="/signup"
      headline="Checking your consent with Microsoft."
      lede="Guardian confirms the grant, then takes you to the next step."
    >
      <p role="status" className="m-0 flex items-center gap-3 text-body text-text-primary">
        <Spinner size={20} />
        This takes a few moments.
      </p>
    </SignInFrame>
  );
}

export default SignUpCallback;
